import { mainEndpoints } from '@/lib/api/endpoints'
import { mainApi } from '@/lib/api/http'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'
import type { PageResult } from '@/lib/api/common'

import type { OptionSet, SaveOptionSetBody } from '../types'

const root = mainEndpoints.optionSets

/**
 * Every option set, with its items.
 *
 * The endpoint is documented as returning a single set, but the service pages —
 * it answers with `{ count, rows }` like the rest of the tree. The envelope is
 * unwrapped defensively so a bare array would still read.
 */
export async function fetchOptionSets(): Promise<OptionSet[]> {
  const page = await mainApi.get<PageResult<OptionSet> | OptionSet[]>(root, {
    params: { limit: PAGE_LIMIT_MAX, offset: 0 },
  })

  return Array.isArray(page) ? page : (page.rows ?? [])
}

export async function fetchOptionSet(id: string): Promise<OptionSet> {
  return await mainApi.get<OptionSet>(`${root}/${id}`)
}

export async function createOptionSet(body: SaveOptionSetBody): Promise<OptionSet> {
  return await mainApi.post<OptionSet>(root, body)
}

/** Update takes the same body as create, items included. */
export async function updateOptionSet(id: string, body: SaveOptionSetBody): Promise<void> {
  await mainApi.put<void>(`${root}/${id}`, body)
}

export async function deleteOptionSet(id: string): Promise<void> {
  await mainApi.delete<void>(`${root}/${id}`)
}

export async function createOptionSetItem(
  setId: string,
  body: { name: { en: string | null; km: string | null } },
): Promise<void> {
  await mainApi.post<void>(`${root}/${setId}/items`, body)
}

export async function deleteOptionSetItem(setId: string, itemId: string): Promise<void> {
  await mainApi.delete<void>(`${root}/${setId}/items/${itemId}`)
}
